import React, { useContext, useEffect, useMemo, useState } from "react"
import { useAppDispatch } from "@/hooks/reduxToolkitHook"
import {
  ActionNotificationContext,
  ActionNotificationContextValeurType,
} from "@/context/ActionNotificationContext"
import { Column } from "@tanstack/react-table"
import { CompanyType1 } from "@/types/CompaniesType"
import { DataTableColumnHeader } from "components/data-table/data-table-column-header"
import { formatMoney, getStatusStyle, } from "@/utils/utils"
import { cn } from "@/utils/shadcn-utils"
import { StatusTranslatedEnum } from "@/constants/CodePromoConstants"
import { useDataTable } from "@/hooks/shadcn/useDataTable"
import { DataTable } from "components/data-table/data-table"
import { DataTableToolbar } from "components/data-table/data-table-toolbar"
import { INCOME_STATUS } from "@/constants/CompaniesConstants"
import { getRevenues } from "@/requests/dashboardRequests"
export interface IncomeProps {

}

function Income(props : IncomeProps) {
  const dispatch = useAppDispatch()
  const { displayAlert } = useContext(
    ActionNotificationContext,
  ) as ActionNotificationContextValeurType

  // const [plansName, setPlansName] = useState<Record<string, string>>({})
  const [clients, setClients] = useState([])
  const INCOME_STATUS_LABEL = Object.keys(INCOME_STATUS).map( key => ({
    value: INCOME_STATUS[key as keyof typeof  INCOME_STATUS],
    label: StatusTranslatedEnum[key as keyof  typeof StatusTranslatedEnum]
  }))

  const columns = useMemo(() => [
    {
      id: "id",
      accessorKey: "id",
      header: ({ column }: { column: Column<CompanyType1, unknown> }) => (
        <DataTableColumnHeader column={column} title="N° Client" className="py-3 px-1"/>
      ),
      cell: ({ row }:{row:any}) => {
        return (
          <div className="flex items-center gap-2 pl-2">
            <span>{String(row.index + 1).padStart(3, '0')}</span>
            {/*{row.original.reference}*/}
          </div>
        )},
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: "N°",
      },
      size: 80,
    },
    {
      id: "company_name",
      accessorKey: "company_name",
      header: ({ column }: { column: Column<CompanyType1, unknown> }) => (
        <DataTableColumnHeader column={column} title="Nom du client" className="py-3 px-1"/>
      ),
      cell: ({ row } :{row:any}) => (
        <div className="flex items-center gap-2 pl-2">
          <span>{row.getValue("company_name")}</span>
        </div>
      ),
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: "Nom du client",
        labelInColumnsAdjustment: "Nom",
        placeholder: "Rechercher un client",
        variant: "text",
        priority: 8,
      },
      filterFn: (row: any, columnId: string, filterValue: string) => {
        // return true
        return row.original.company_name.toLowerCase().includes(filterValue.toLowerCase());
      },
      size: 180,
    },
    {
      id: "wallet_balance",
      accessorKey: "wallet_balance",
      header: ({ column }: { column: Column<CompanyType1, unknown> }) => (
        <DataTableColumnHeader column={column} title="Solde wallet" className="py-3 px-1"/>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2 pl-2">
          {formatMoney(row.getValue("wallet_balance") ?? 0)}
        </div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
      meta: {
        label: "Solde wallet",
      },
      size: 120,
    },
    {
      id: "total_revenue",
      accessorKey: "total_revenue",
      header: ({ column }: { column: Column<CompanyType1, unknown> }) => (
        <DataTableColumnHeader column={column} title="Revenu Total" className="py-3 px-1"/>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2 pl-2">
          {formatMoney(row.getValue("total_revenue") ?? 0)}
        </div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
      meta: {
        label: "Revenu Total",
      },
      size: 120,
    },
    {
      id: "total_collected",
      accessorKey: "total_collected",
      header: ({ column }: { column: Column<CompanyType1, unknown> }) => (
        <DataTableColumnHeader column={column} title="Montant Encaissé" className="py-3 px-1"/>
      ),
      cell: ({ row }) => (
        <div className="flex items-center gap-2 pl-2">
          {formatMoney(row.getValue("total_collected") ?? 0)}
        </div>
      ),
      enableColumnFilter: false,
      enableSorting: false,
      meta: {
        label: "Montant Encaissé",
      },
      size: 120,
    },
    {
      id: "amount_due",
      accessorKey: "amount_due",
      header: ({ column }: { column: Column<CompanyType1, unknown> }) => (
        <DataTableColumnHeader column={column} title="Montant dû" className="py-3 px-1"/>
      ),
      cell: ({ row } :{row: any} ) => {
        return (
          <div className="flex items-center gap-2 pl-2">
            {row.getValue("amount_due") ?? 0}
          </div>
        )
      },
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: "Montant dû",
        variant: "multiSelect",
        options: [
          {
            label: "Non Soldé",
            value: true,
          },
          {
            label: "Soldé",
            value: false,
          }
        ]
      },
      filterFn: (row: any, columnId: string, filterValue: boolean[]) => {
        return filterValue.includes((row.getValue("amount_due") ?? 0) > 0);
      },
      size: 120,
    },
    {
      id: "payment_status",
      accessorKey: "payment_status",
      header: ({ column }: { column: Column<CompanyType1, unknown> }) => (
        <DataTableColumnHeader column={column} title="Statut" className="py-3 px-1"/>
      ),
      cell: ({ row } :{row: any} ) => {
        return (
          <div className="flex items-center gap-2 pl-2">
            <div
              className={
                cn(
                  " tag",
                  getStatusStyle(row.getValue('payment_status') ?? "INACTIVE"),
                )}>
              {StatusTranslatedEnum[row.getValue('payment_status') as keyof typeof  StatusTranslatedEnum]}
            </div>
          </div>
        )
      },
      enableColumnFilter: true,
      enableSorting: true,
      meta: {
        label: "Statut",
        variant: "multiSelect",
        options: INCOME_STATUS_LABEL
      },
      filterFn: (row: any, columnId: string, filterValue: boolean[]) => {
        return filterValue.includes(row.getValue('payment_status'));
      },
      size: 100,
    },
    // {
    //   id: "actions",
    //   header: () => "Actions",
    //   cell: ({ row } :{row: any} ) => {
    //     return (
    //       <DropdownMenu>
    //         <DropdownMenuTrigger asChild>
    //           <Button variant="ghost" size="icon">
    //             <Ellipsis />
    //             <span className="sr-only">Actions</span>
    //           </Button>
    //         </DropdownMenuTrigger>
    //         <DropdownMenuContent align="end">
    //           <DropdownMenuItem>
    //             <Link
    //               to={replaceIdInLink(
    //                 row.original.id,
    //                 CLIENT_INFOS_OVERVIEW_ROUTE
    //               )}>
    //               Voir le client
    //             </Link>
    //           </DropdownMenuItem>
    //           <DropdownMenuItem>
    //             <Link
    //               to={replaceIdInLink(row.original.id, CLIENT_UPDATE_STEPPERS_ROUTE )}
    //             >
    //               Modifier le client
    //             </Link>
    //           </DropdownMenuItem>
    //           {!row.original.subscription && row.original.is_active && (
    //             <DropdownMenuItem>
    //               <Link
    //                 to={replaceIdInLink(
    //                   row.original.id,
    //                   CLIENT_ABONNEMENT_INFOS_ROUTE
    //                 )}
    //                 state={{ addSubscription: true }}>
    //                 Créer un abonnement
    //               </Link>
    //             </DropdownMenuItem>
    //           )}
    //           {row.original.subscription && row.original.subscription.payment_status === StatusEnum.INACTIVE && row.original.is_active && (
    //             <DropdownMenuItem>
    //               <Link
    //                 to={replaceIdInLink(
    //                   row.original.id,
    //                   CLIENT_ABONNEMENT_INFOS_ROUTE
    //                 )}
    //                 state={{ updateSubscription: true }}>
    //                 Modifier l'abonnement
    //               </Link>
    //             </DropdownMenuItem>
    //           )}
    //           {row.original.is_active ? (
    //             <DropdownMenuItem>
    //               <div
    //                 className="text-red2-800"
    //                 onClick={() => {
    //                   document.body.style.pointerEvents = "auto"
    //                   handleOpen(
    //                     ClientActionFormChoices.DESACTIVATE,
    //                     row.original.id
    //                   )
    //                 }}>
    //                 Désactiver le client
    //               </div>
    //             </DropdownMenuItem>
    //           ) : (
    //             <DropdownMenuItem>
    //               <div
    //                 onClick={() => {
    //                   document.body.style.pointerEvents = "auto"
    //                   handleOpen(
    //                     ClientActionFormChoices.ACTIVATE,
    //                     row.original.id
    //                   )
    //                 }}>
    //                 Activer le client
    //               </div>
    //             </DropdownMenuItem>
    //           )}
    //           {row.original.subscription?.payment_status === "ACTIVE" &&
    //             row.original.is_active && (
    //               <>
    //                 <DropdownMenuItem>
    //                   <Link
    //                     to={replaceIdInLink(
    //                       row.original.id,
    //                       CLIENT_INFOS_OVERVIEW_ROUTE
    //                     )}
    //                     state={{ deleteRecovered: row.original.id }}>
    //                     Suspendre les recouvrements
    //                   </Link>
    //                 </DropdownMenuItem>
    //                 <DropdownMenuItem>
    //                   <div
    //                     // to={replaceIdInLink(row.original.id, CLIENT_ABONNEMENT_INFOS_ROUTE)}
    //                     // state={{ deleteSubscription: row.original.id }}
    //                     onClick={() => {
    //                       document.body.style.pointerEvents = "auto"
    //                       handleOpen(
    //                         ClientActionFormChoices.RESILIATE,
    //                         row.original.id
    //                       )
    //                     }}
    //                     className="text-red2-800">
    //                     Résilier l'abonnement client
    //                   </div>
    //                 </DropdownMenuItem>
    //               </>
    //             )}
    //         </DropdownMenuContent>
    //       </DropdownMenu>
    //     )
    //   },
    //   size: 35,
    // },
  ], [])

  const table = useDataTable({
    data: clients,
    columns: columns,
    pageSize: 10,
    enableSorting: true,
    // enableFiltering: true,
    // enableExpanding: true,
    initialColumnPinning: { right: ["payment_status"] },
    // initialFilters: [],
  })

  useEffect(() =>{
    getRevenues().then(({ data }) => {
      setClients(data)
    })
  }, [])

  return (
    <div className="card">
      <DataTable table={table} noDataMessage="Aucun client défini" tableLayout="fixed">
        <DataTableToolbar table={table} />
      </DataTable>
    </div>
  )
}

export default Income